import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import authService from '../services/authService';
import Layout from '../components/Layout';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertCircle,
  Contact2,
  Download,
  ExternalLink,
  HelpCircle,
  MessageCircle,
  Phone,
  PhoneForwarded,
  Printer,
} from 'lucide-react';
import { toast } from 'sonner';
import QrTemplatePickerDialog from '@/components/QrTemplatePickerDialog';
import SupportTicketsPanel from '@/components/support-tickets/SupportTicketsPanel';
import ServiceGroupsGuide from '@/components/leistungen/ServiceGroupsGuide';

type HelpTab = 'qr' | 'rufumleitung' | 'nummern' | 'leistungen' | 'anleitungen' | 'tickets';

const HELP_TABS: { id: HelpTab; label: string }[] = [
  { id: 'qr', label: 'QR-Code-Generator' },
  { id: 'rufumleitung', label: 'Rufumleitung einrichten' },
  { id: 'nummern', label: 'Rufnummern & Salon-Zuteilung' },
  { id: 'leistungen', label: 'Leistungen' },
  { id: 'anleitungen', label: 'Anleitungen' },
  { id: 'tickets', label: 'Support-Tickets' },
];

type QrMode = 'whatsapp' | 'vcard';

interface ProviderGuide {
  name: string;
  initial: string;
  color: string;
  tint: string;
  tagline: string;
  portalUrl: string;
  portalLabel: string;
  steps: string[];
  logoSlug?: string;
  logoDomain?: string;
}

const providerGuides: ProviderGuide[] = [
  {
    name: 'Telekom',
    initial: 'T',
    color: '#E20074',
    tint: '#FCE4F1',
    tagline: 'Festnetz & Mobilfunk',
    portalUrl: 'https://www.telekom.de/kundencenter',
    portalLabel: 'Zum Telekom Kundencenter',
    logoSlug: 'deutschetelekom',
    logoDomain: 'telekom.de',
    steps: [
      'Im Kundencenter einloggen (oder in der MeinMagenta-App).',
      'Bereich „Festnetz" bzw. „Mobilfunk" → Einstellungen → Rufumleitung wählen.',
      'Rufnummer des Telefonagenten eintragen und Bedingung wählen (sofort / besetzt / nicht erreichbar).',
      'Speichern – Weiterleitung ist innerhalb weniger Minuten aktiv.',
    ],
  },
  {
    name: 'Vodafone',
    initial: 'V',
    color: '#E60000',
    tint: '#FDE3E3',
    tagline: 'Kabel, DSL & Mobilfunk',
    portalUrl: 'https://www.vodafone.de/meinvodafone/account',
    portalLabel: 'Zu MeinVodafone',
    logoSlug: 'vodafone',
    logoDomain: 'vodafone.de',
    steps: [
      'Bei MeinVodafone anmelden, zum Vertrag wechseln (Festnetz oder Mobil).',
      'Unter „Einstellungen → Rufumleitung" Ziel eintragen.',
      'Bei Vodafone Kabel: im Router (Station) unter Telefonie → Rufumleitung verwalten.',
      'Änderung bestätigen und kurz testen.',
    ],
  },
  {
    name: '1&1',
    initial: '1',
    color: '#004899',
    tint: '#E0E9F5',
    tagline: 'DSL, Glasfaser & Mobilfunk',
    portalUrl: 'https://control-center.1und1.de/',
    portalLabel: 'Zum 1&1 Control-Center',
    logoDomain: '1und1.de',
    steps: [
      'Im Control-Center anmelden und den Vertrag auswählen.',
      '„Telefonie → Rufnummern → Rufumleitung einrichten" öffnen.',
      'Agenten-Rufnummer mit Ländervorwahl eintragen, Auslöser wählen.',
      'Alternativ in der Fritz!Box unter Telefonie → Rufbehandlung schalten.',
    ],
  },
  {
    name: 'o2 / Telefónica',
    initial: 'O',
    color: '#0019A5',
    tint: '#E0E4F5',
    tagline: 'Mobilfunk & Festnetz',
    portalUrl: 'https://login.o2online.de/auth/login',
    portalLabel: 'Zu Mein o2',
    logoSlug: 'o2',
    logoDomain: 'o2online.de',
    steps: [
      'Bei Mein o2 einloggen und Vertrag wählen.',
      '„Einstellungen → Rufumleitungen" öffnen.',
      'Zielrufnummer (Agent) eintragen und Bedingung wählen.',
      'Aktivieren – Umleitung greift ab dem nächsten Anruf.',
    ],
  },
  {
    name: 'Sipgate',
    initial: 'S',
    color: '#E8342F',
    tint: '#FDE3E2',
    tagline: 'Business-VoIP & Cloud-Telefonie',
    portalUrl: 'https://app.sipgate.com/',
    portalLabel: 'Zur Sipgate Web-App',
    logoDomain: 'sipgate.de',
    steps: [
      'In der Sipgate Web-App anmelden (app.sipgate.com).',
      'Links auf „Anschlüsse → Rufweiterleitung" klicken.',
      'Neue Regel anlegen: Ziel = Agent-Nummer, optional Zeitplan.',
      'Regel aktivieren – ideal für zeitbasierte Umleitung (z.&nbsp;B. nach Feierabend).',
    ],
  },
  {
    name: 'Fritz!Box (AVM)',
    initial: 'F',
    color: '#C00000',
    tint: '#FCE0E0',
    tagline: 'Router – anbieterunabhängig',
    portalUrl: 'http://fritz.box/',
    portalLabel: 'Fritz!Box-Oberfläche öffnen',
    logoDomain: 'avm.de',
    steps: [
      'Im Browser fritz.box öffnen und anmelden.',
      '„Telefonie → Rufbehandlung → Rufumleitung" wählen.',
      '„Neue Rufumleitung" → von welcher internen Nummer auf Agent-Nummer.',
      'Optional: Zeitplan hinterlegen (z.&nbsp;B. 18:00–09:00 und Wochenende).',
    ],
  },
];

// Der Light-Tint ist eine feste Pastellfarbe pro Anbieter; im Dark Mode
// waere die auf dunklem Grund unlesbar. Statt einen zweiten Farbsatz zu
// pflegen, wird die Markenfarbe selbst mit niedriger Deckkraft auf die
// Karte gelegt – bleibt farblich erkennbar, ohne hell zu blitzen.
const getProviderTint = (provider: ProviderGuide, resolvedTheme: 'light' | 'dark'): string =>
  resolvedTheme === 'dark' ? `${provider.color}26` : provider.tint;

const ProviderLogo: React.FC<{ provider: ProviderGuide; size?: number }> = ({ provider, size = 40 }) => {
  const sources: string[] = [];
  if (provider.logoSlug) {
    sources.push(`https://cdn.simpleicons.org/${provider.logoSlug}/${provider.color.replace('#', '')}`);
  }
  if (provider.logoDomain) {
    sources.push(`https://www.google.com/s2/favicons?sz=128&domain=${provider.logoDomain}`);
  }
  const [index, setIndex] = useState(0);
  const src = sources[index];
  if (!src) {
    return (
      <div
        className="rounded-full flex items-center justify-center shrink-0 text-white font-bold"
        style={{ width: size, height: size, backgroundColor: provider.color, fontSize: size * 0.45 }}
      >
        {provider.initial}
      </div>
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0 bg-card shadow-sm border border-border overflow-hidden"
      style={{ width: size, height: size }}
    >
      <img
        src={src}
        alt={provider.name}
        style={{ width: size * 0.6, height: size * 0.6, objectFit: 'contain' }}
        onError={() => setIndex(i => i + 1)}
      />
    </div>
  );
};

interface BusinessDetails {
  business_name: string;
  whatsapp_number: string;
  phone_number: string;
  email: string;
}

function normalizePhoneForWaMe(raw: string): string {
  return (raw || '').replace(/\D/g, '');
}

function buildWhatsAppLink(whatsappNumber: string, prefilledText: string): string {
  const digits = normalizePhoneForWaMe(whatsappNumber);
  const base = `https://wa.me/${digits}`;
  if (prefilledText.trim()) {
    return `${base}?text=${encodeURIComponent(prefilledText.trim())}`;
  }
  return base;
}

function buildVCard(params: {
  name: string;
  org?: string;
  whatsappNumber: string;
  phoneNumber?: string;
  email?: string;
}): string {
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${params.name}`,
  ];
  if (params.org) {
    lines.push(`ORG:${params.org}`);
  }
  const waDigits = normalizePhoneForWaMe(params.whatsappNumber);
  if (waDigits) {
    lines.push(`TEL;TYPE=CELL,VOICE:+${waDigits}`);
  }
  if (params.phoneNumber) {
    const phoneDigits = normalizePhoneForWaMe(params.phoneNumber);
    if (phoneDigits && phoneDigits !== waDigits) {
      lines.push(`TEL;TYPE=WORK,VOICE:+${phoneDigits}`);
    }
  }
  if (params.email) {
    lines.push(`EMAIL;TYPE=WORK:${params.email}`);
  }
  lines.push('END:VCARD');
  return lines.join('\n');
}

/**
 * Guide for "Rufnummern & Salon-Zuteilung".
 *
 * Written for salon owners, not for us: no siteCd, no "Scope", no "Agent-
 * Konfiguration". The question-mark icon on the profile card links here, so
 * this has to answer "what is this screen for and what do I click" on its own.
 */
const NumberScopeGuide: React.FC = () => {
  const steps: { title: string; body: React.ReactNode }[] = [
    {
      title: 'Zweite Rufnummer holen',
      body: (
        <>
          Im{' '}
          <Link to="/profile?tab=nummern" className="text-[#1967D2] hover:underline">
            Unternehmensprofil → Rufnummern &amp; Salon-Zuteilung
          </Link>{' '}
          auf <strong>Weitere Rufnummer hinzufügen</strong> klicken, Land wählen, eine
          freie Nummer anklicken und auf <strong>Nummer reservieren</strong> gehen. Die
          Nummer erscheint danach sofort in der Liste. Deine bisherige Nummer bleibt
          unverändert.
        </>
      ),
    },
    {
      title: 'Salons zuordnen',
      body: (
        <>
          Unter jeder Nummer stehen alle deine Salons als Schaltflächen. Klick die an,
          für die diese Nummer zuständig sein soll – <strong>blau heißt zuständig</strong>.
          Du kannst einer Nummer auch mehrere Salons geben, zum Beispiel zwei Filialen in
          derselben Stadt. Wählst du gar nichts aus, betreut die Nummer alle Salons.
        </>
      ),
    },
    {
      title: 'Begrüßungsnamen vergeben',
      body: (
        <>
          Mit diesem Namen meldet sich der Agent am Telefon: „Hallo, ich bin Lisa von
          <em> Salon Köln</em>…". Lass das Feld leer, wenn dein Unternehmensname passt.
          Danach auf <strong>Speichern</strong> – die Änderung gilt ab dem nächsten Anruf.
        </>
      ),
    },
    {
      title: 'WhatsApp entscheiden: eine Nummer oder mehrere',
      body: (
        <>
          Im selben Bereich steht unter <strong>WhatsApp-Agenten</strong> die Wahl:
          eine zentrale WhatsApp-Nummer für alle Salons – dann ist hier nichts weiter
          zu tun – oder <strong>eine eigene Nummer je Standort</strong>. Bei der
          zweiten Variante verbindest du über <strong>Weitere WhatsApp-Nummer
          verbinden</strong> ein zusätzliches WhatsApp-Konto; das läuft über dasselbe
          Meta-Fenster wie bei der Einrichtung, und deine bestehende Verbindung
          bleibt unangetastet. Die Salons jeder WhatsApp-Nummer klickst du direkt dort
          an und speicherst – einen Telefonagenten brauchst du dafür nicht.
        </>
      ),
    },
    {
      title: 'Anrufe auf die neue Nummer umleiten',
      body: (
        <>
          Damit Kunden weiterhin die Salon-Nummer wählen können, richte im Telefonanschluss
          dieses Standorts eine Rufumleitung auf die neue Nummer ein. Wie das bei deinem
          Anbieter geht, steht im Reiter{' '}
          <Link to="/help?tab=rufumleitung" className="text-[#1967D2] hover:underline">
            Rufumleitung einrichten
          </Link>
          .
        </>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <Card className="shadow-sm border-border">
        <CardHeader>
          <div className="flex items-start gap-3">
            <Phone className="h-5 w-5 text-[#1967D2] mt-0.5 shrink-0" />
            <div>
              <CardTitle className="text-lg">Rufnummern &amp; Salon-Zuteilung</CardTitle>
              <CardDescription>
                Für Unternehmen mit mehreren Salons: Jede Rufnummer bekommt ihren eigenen
                Agenten, der nur für bestimmte Standorte bucht und berät.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border border-border bg-muted/50 p-4 space-y-2">
            <p className="text-sm font-medium text-foreground">Brauche ich das überhaupt?</p>
            <p className="text-sm text-muted-foreground">
              Hast du <strong>einen Salon</strong>, ist hier nichts zu tun – deine Nummer
              betreut ihn automatisch. Interessant wird es ab dem{' '}
              <strong>zweiten Standort</strong>: Statt eines Agenten, der alle Filialen
              kennen muss, bekommt jede Stadt ihre eigene Nummer und damit ihren eigenen
              Agenten. Das Gespräch wird kürzer, weil niemand mehr gefragt werden muss,
              welche Filiale gemeint ist.
            </p>
          </div>

          <ol className="space-y-5">
            {steps.map((step, i) => (
              <li key={step.title} className="flex gap-4">
                <span className="shrink-0 h-8 w-8 rounded-full bg-[#1967D2] text-white text-sm font-semibold flex items-center justify-center">
                  {i + 1}
                </span>
                <div className="space-y-1 pt-1">
                  <p className="font-medium text-foreground">{step.title}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      <Card className="shadow-sm border-border">
        <CardHeader>
          <CardTitle className="text-lg">Was der Anrufer davon merkt</CardTitle>
          <CardDescription>Ein Beispiel mit zwei Standorten.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-border p-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Beispiel:</strong> Die Nummer …733 ist
              dem Salon Bonn zugeordnet, die Nummer …280 dem Salon Köln.
            </p>
            <div className="space-y-2 text-sm">
              <p className="text-muted-foreground">
                <span className="font-medium text-foreground">Anruf auf …733:</span> Der
                Agent meldet sich als Bonn, kennt nur die Bonner Leistungen und Zeiten und
                fragt gar nicht erst, welcher Salon gemeint ist.
              </p>
              <p className="text-muted-foreground">
                <span className="font-medium text-foreground">
                  Kunde fragt nach Köln:
                </span>{' '}
                Der Agent bietet an zu verbinden und stellt direkt auf …280 durch – dort
                geht der Kölner Agent ran und bucht. In WhatsApp nennt er stattdessen die
                zuständige Nummer.
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              Diese Weiterleitung entsteht automatisch aus der Zuteilung. Du musst dafür
              nichts eintragen. In WhatsApp gilt dasselbe: Hat der andere Standort eine
              eigene WhatsApp-Nummer, nennt der Agent sie; sonst betreut die zentrale
              Nummer weiterhin alle Salons.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm border-border">
        <CardHeader>
          <CardTitle className="text-lg">Häufige Fragen</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" className="w-full">
            <AccordionItem value="wieviele">
              <AccordionTrigger>Brauche ich für jeden Salon eine eigene Nummer?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground space-y-2">
                <p>
                  Nein. Eine Nummer kann beliebig viele Salons betreuen. Sinnvoll ist eine
                  eigene Nummer dort, wo Kunden ohnehin die Filiale direkt anrufen – meist
                  also pro Stadt.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="keine-auswahl">
              <AccordionTrigger>Was passiert, wenn ich keinen Salon auswähle?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Dann betreut die Nummer alle Salons – genau wie vorher. Nichts geht
                verloren, wenn du die Zuteilung leer lässt.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="unterschiedlich">
              <AccordionTrigger>
                Kann jede Nummer eigene Einstellungen haben – Stimme, Begrüßung, Cross-Selling?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground space-y-2">
                <p>
                  Ja. Sobald du mehr als eine Nummer hast, erscheint auf der Seite{' '}
                  <Link to="/voice-agent" className="text-[#1967D2] hover:underline">
                    Agenten
                  </Link>{' '}
                  oben rechts ein Umschalter. Dort wählst du die Nummer aus und stellst
                  alles nur für diesen Agenten ein.
                </p>
                <p>
                  Felder, die du dort nicht änderst, übernehmen automatisch deine
                  unternehmensweite Einstellung.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="wa-gemeinsam">
              <AccordionTrigger>
                Eine Nummer bedient Telefon und WhatsApp – gilt die Zuteilung für beides?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground space-y-2">
                <p>
                  Ja. Eine Rufnummer ist ein Agent, egal ob jemand anruft oder schreibt –
                  die zugeteilten Salons gelten für beide Wege. Du kannst die Auswahl beim
                  Telefon-Agenten oder im WhatsApp-Bereich ändern; es ist dieselbe Auswahl,
                  beide Stellen zeigen immer dasselbe.
                </p>
                <p>
                  Soll WhatsApp alle Salons bedienen, während der Telefonagent nur einen
                  Standort betreut, nimm dafür zwei getrennte Nummern.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="wa-ohne-telefon">
              <AccordionTrigger>
                Ich nutze nur WhatsApp, keinen Telefonagenten – kann ich trotzdem Salons
                zuteilen?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground space-y-2">
                <p>
                  Ja. Unter <strong>WhatsApp-Agenten</strong> stehen bei jeder
                  WhatsApp-Nummer deine Salons als Schaltflächen. Anklicken, auf{' '}
                  <strong>Speichern</strong> gehen – fertig. Jede WhatsApp-Nummer hat ihre
                  eigene Auswahl, unabhängig davon, ob und welche Telefonnummern es gibt.
                </p>
                <p>
                  Schreibt jemand an eine Nummer, die seinen Salon nicht betreut, bucht der
                  Agent dort nicht, sondern nennt die Nummer, die für diesen Salon
                  zuständig ist.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="wa-trennen">
              <AccordionTrigger>Kann ich eine WhatsApp-Anbindung wieder lösen?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Ja, über <strong>Verbindung trennen</strong> bei der jeweiligen Nummer.
                Die Nummer bleibt dann Telefon-Agent mit unveränderter Salon-Zuteilung,
                nur der WhatsApp-Teil fällt weg. Die WhatsApp-Hauptnummer des Kontos wird
                stattdessen in den WhatsApp-Einstellungen gelöst.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="wirksam">
              <AccordionTrigger>Wann wird eine Änderung wirksam?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Sofort – spätestens beim übernächsten Anruf. Ein laufendes Gespräch behält
                die Einstellungen, mit denen es begonnen hat.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="ch-at">
              <AccordionTrigger>Warum kann ich keine Schweizer oder österreichische Nummer kaufen?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Für die Schweiz und Österreich verlangt der Netzbetreiber einen
                freigegebenen Nachweis über Unternehmen und Adresse. Solange dieser
                aussteht, lässt sich dort keine Nummer buchen. Melde dich in dem Fall beim
                Support – wir kümmern uns darum.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="verlaeufe">
              <AccordionTrigger>Sehe ich, welcher Agent ein Gespräch geführt hat?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Ja. Unter{' '}
                <Link to="/conversations" className="text-[#1967D2] hover:underline">
                  Verläufe
                </Link>{' '}
                kannst du oben nach Agent filtern und dir nur die Gespräche einer Nummer
                anzeigen lassen.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
};

const HelpPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { resolvedTheme } = useTheme();
  const navigate = useNavigate();
  // ?tab=… so the help icons inside the app can link to the right guide.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: HelpTab =
    (HELP_TABS.find((t) => t.id === searchParams.get('tab'))?.id) || 'qr';
  const selectTab = (tab: HelpTab) => {
    // Frisch aufbauen: Ticket-Parameter (ticket, new, c) gehören nur zum
    // Ticket-Tab und dürfen beim Wechsel nicht hängen bleiben.
    const next = new URLSearchParams();
    if (tab !== 'qr') next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState<BusinessDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [mode, setMode] = useState<QrMode>('whatsapp');
  const [prefilledText, setPrefilledText] = useState('Hallo, ich möchte gerne einen Termin buchen.');
  const [contactName, setContactName] = useState('');
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const qrRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const data = await authService.getBusinessProfile();
        setBusiness({
          business_name: data.business_name ?? '',
          whatsapp_number: data.whatsapp_number ?? '',
          phone_number: data.phone_number ?? '',
          email: data.email ?? '',
        });
        setContactName(data.business_name ?? '');
      } catch (err) {
        console.error('Error loading business profile:', err);
        setError('Profildaten konnten nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthenticated, navigate]);

  const qrValue = useMemo(() => {
    if (!business) return '';
    if (mode === 'whatsapp') {
      return buildWhatsAppLink(business.whatsapp_number, prefilledText);
    }
    return buildVCard({
      name: contactName || business.business_name,
      org: business.business_name,
      whatsappNumber: business.whatsapp_number,
      phoneNumber: business.phone_number,
      email: business.email,
    });
  }, [business, mode, prefilledText, contactName]);

  const handleOpenTemplateDialog = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) {
      toast.error('QR-Code noch nicht bereit.');
      return;
    }
    setTemplateDialogOpen(true);
  };

  const handlePrint = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) {
      toast.error('QR-Code noch nicht bereit.');
      return;
    }
    const dataUrl = canvas.toDataURL('image/png');
    const w = window.open('', '_blank', 'width=600,height=700');
    if (!w) {
      toast.error('Pop-up blockiert. Bitte Pop-ups erlauben.');
      return;
    }
    const title = mode === 'whatsapp'
      ? `Termin buchen per WhatsApp – ${business?.business_name || ''}`
      : `Kontakt speichern – ${business?.business_name || ''}`;
    const subtitle = mode === 'whatsapp'
      ? 'Scannen und direkt chatten'
      : 'Scannen und Kontakt speichern';
    w.document.write(`<!doctype html><html><head><meta charset="utf-8" /><title>${title}</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 40px; text-align: center; color: #0f172a; }
        h1 { font-size: 28px; margin: 0 0 8px; }
        p { color: #475569; margin: 0 0 24px; }
        img { width: 320px; height: 320px; }
        .footer { margin-top: 24px; font-size: 14px; color: #64748b; }
      </style></head><body>
        <h1>${title}</h1>
        <p>${subtitle}</p>
        <img src="${dataUrl}" alt="QR Code" />
        <div class="footer">${business?.whatsapp_number ? `WhatsApp: ${business.whatsapp_number}` : ''}</div>
        <script>window.onload = () => { window.focus(); window.print(); };</script>
      </body></html>`);
    w.document.close();
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col justify-center items-center min-h-[60vh] gap-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-primary/25 rounded-full"></div>
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full absolute top-0 left-0 animate-spin"></div>
          </div>
          <p className="text-muted-foreground text-sm animate-pulse">Hilfe wird geladen...</p>
        </div>
      </Layout>
    );
  }

  const hasWhatsApp = Boolean(business?.whatsapp_number);

  return (
    <Layout>
      <div className="container max-w-5xl mx-auto py-8 space-y-8 animate-in fade-in duration-500">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-2">
              <HelpCircle className="h-7 w-7 text-[#1967D2]" />
              Hilfe & Anleitungen
            </h1>
            <p className="text-muted-foreground mt-1">
              QR-Codes für den Salon, Rufumleitung, Rufnummern-Zuteilung, Anleitungen und Support-Tickets.
            </p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex gap-1 border-b border-border overflow-x-auto overflow-y-hidden">
          {HELP_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => selectTab(tab.id)}
              className={
                activeTab === tab.id
                  ? 'px-4 py-2 text-sm font-medium border-b-2 -mb-px border-[#1967D2] text-[#1967D2] whitespace-nowrap'
                  : 'px-4 py-2 text-sm font-medium border-b-2 -mb-px border-transparent text-muted-foreground hover:text-foreground whitespace-nowrap'
              }
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'tickets' && <SupportTicketsPanel />}

        {activeTab === 'qr' && (
        <Card className="shadow-sm border-border">
          <CardHeader>
            <CardTitle className="text-lg">QR-Code-Generator</CardTitle>
            <CardDescription>
              Erzeugen Sie einen QR-Code, den Sie im Salon auslegen können – Kunden scannen und landen direkt im WhatsApp-Chat
              oder speichern Sie als Kontakt ab.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {!hasWhatsApp && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Es ist noch keine WhatsApp-Nummer hinterlegt. Bitte zuerst in der Einrichtung verbinden.
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setMode('whatsapp')}
                className={`text-left rounded-lg border-2 p-4 transition-all ${
                  mode === 'whatsapp'
                    ? 'border-[#1967D2] bg-primary/10'
                    : 'border-border bg-card hover:border-muted-foreground/40'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <MessageCircle className="h-5 w-5 text-[#1967D2]" />
                  <span className="font-semibold text-foreground">WhatsApp-Chat öffnen</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Scan führt direkt in den WhatsApp-Chat mit Ihrem Termin-Assistenten – optional mit vorausgefülltem Text.
                </p>
              </button>

              <button
                type="button"
                onClick={() => setMode('vcard')}
                className={`text-left rounded-lg border-2 p-4 transition-all ${
                  mode === 'vcard'
                    ? 'border-[#1967D2] bg-primary/10'
                    : 'border-border bg-card hover:border-muted-foreground/40'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Contact2 className="h-5 w-5 text-[#1967D2]" />
                  <span className="font-semibold text-foreground">Kontakt speichern (vCard)</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Scan speichert Ihren Salon als neuen Kontakt auf dem Smartphone des Kunden.
                </p>
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              <div className="space-y-4">
                {mode === 'whatsapp' ? (
                  <>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                        Vorausgefüllter Text (optional)
                      </Label>
                      <Textarea
                        value={prefilledText}
                        onChange={(e) => setPrefilledText(e.target.value)}
                        rows={3}
                        placeholder="Hallo, ich möchte gerne einen Termin buchen."
                        className="bg-muted border-border resize-none"
                      />
                      <p className="text-xs text-muted-foreground">
                        Wird im WhatsApp-Eingabefeld vorgeschlagen. Leer lassen für leeren Chat.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                        WhatsApp-Nummer
                      </Label>
                      <Input value={business?.whatsapp_number || ''} disabled className="bg-muted border-border font-mono" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                        Ziel-Link
                      </Label>
                      <Input value={qrValue} disabled className="bg-muted border-border text-xs" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                        Kontaktname
                      </Label>
                      <Input
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        placeholder="Salon-Name"
                        className="bg-muted border-border"
                      />
                      <p className="text-xs text-muted-foreground">
                        So wird der Kontakt im Adressbuch Ihrer Kunden angezeigt.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                        Enthaltene Daten
                      </Label>
                      <div className="bg-muted border border-border rounded-md p-3 text-sm text-muted-foreground space-y-1">
                        <div>Name: <span className="font-medium">{contactName || business?.business_name || '—'}</span></div>
                        <div>WhatsApp: <span className="font-mono">{business?.whatsapp_number || '—'}</span></div>
                        {business?.phone_number && (
                          <div>Telefon: <span className="font-mono">{business.phone_number}</span></div>
                        )}
                        {business?.email && (
                          <div>E-Mail: <span className="font-mono">{business.email}</span></div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="flex flex-col items-center gap-4">
                <div
                  ref={qrRef}
                  className="bg-white p-6 border-2 border-border rounded-xl shadow-sm"
                >
                  {qrValue ? (
                    <QRCodeCanvas
                      value={qrValue}
                      size={256}
                      level="M"
                      includeMargin={false}
                      bgColor="#ffffff"
                      fgColor="#0f172a"
                    />
                  ) : (
                    <div className="w-64 h-64 flex items-center justify-center text-muted-foreground text-sm text-center">
                      Keine Daten für QR-Code verfügbar
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Button
                    onClick={handleOpenTemplateDialog}
                    disabled={!qrValue}
                    className="bg-[#1967D2] hover:bg-[#1558b0]"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    PNG herunterladen
                  </Button>
                  <Button variant="outline" onClick={handlePrint} disabled={!qrValue}>
                    <Printer className="h-4 w-4 mr-2" />
                    Drucken
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground text-center max-w-xs">
                  Tipp: Im Salon gut sichtbar aufstellen – z.&nbsp;B. am Empfang, auf dem Spiegel oder auf der Rechnung.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {activeTab === 'rufumleitung' && (
        <Card className="shadow-sm border-border overflow-hidden">
          <Accordion type="single" collapsible defaultValue="rufumleitung">
            <AccordionItem value="rufumleitung" className="border-none">
              <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-muted/50 [&[data-state=open]]:border-b [&[data-state=open]]:border-border">
                <div className="flex items-center gap-4 flex-1 text-left">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <PhoneForwarded className="h-6 w-6 text-[#1967D2]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-lg font-semibold text-foreground">Rufumleitung einrichten</div>
                    <div className="text-sm text-muted-foreground truncate">
                      GSM-Codes &amp; Anbieter-Portale – Anrufe auf den Telefonagenten weiterleiten.
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center -space-x-2 pr-2">
                    {providerGuides.slice(0, 5).map((p) => (
                      <div key={p.name} className="ring-2 ring-background rounded-full">
                        <ProviderLogo provider={p} size={32} />
                      </div>
                    ))}
                    <div className="h-8 w-8 rounded-full bg-muted border-2 border-background flex items-center justify-center text-xs font-semibold text-muted-foreground">
                      +{providerGuides.length - 5}
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6 pt-4 space-y-6">
                <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                Die Rufnummer Ihres Telefonagenten finden Sie unter <strong>Agenten → Telefonagent</strong>. Kopieren
                Sie sie, bevor Sie die Weiterleitung im Portal Ihres Anbieters anlegen.
              </AlertDescription>
            </Alert>

            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3">Schnell via Telefon (Handy &amp; Festnetz mit Tastenwahl)</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Diese GSM-Codes funktionieren bei allen deutschen Mobilfunk- &amp; vielen Festnetzanbietern.
                <em className="ml-1">Zielnummer</em> = Rufnummer Ihres Telefonagenten (mit Ländervorwahl, z.&nbsp;B. +49&nbsp;…).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div className="flex items-start gap-2 p-3 bg-muted rounded-md border border-border">
                  <span className="font-mono text-xs bg-card px-2 py-0.5 rounded border">**21*ZIEL#</span>
                  <span className="text-muted-foreground">Sofortige Weiterleitung aller Anrufe</span>
                </div>
                <div className="flex items-start gap-2 p-3 bg-muted rounded-md border border-border">
                  <span className="font-mono text-xs bg-card px-2 py-0.5 rounded border">**61*ZIEL#</span>
                  <span className="text-muted-foreground">Nur wenn Sie nicht abheben</span>
                </div>
                <div className="flex items-start gap-2 p-3 bg-muted rounded-md border border-border">
                  <span className="font-mono text-xs bg-card px-2 py-0.5 rounded border">**67*ZIEL#</span>
                  <span className="text-muted-foreground">Nur bei besetzter Leitung</span>
                </div>
                <div className="flex items-start gap-2 p-3 bg-muted rounded-md border border-border">
                  <span className="font-mono text-xs bg-card px-2 py-0.5 rounded border">**62*ZIEL#</span>
                  <span className="text-muted-foreground">Nur wenn Sie nicht erreichbar sind</span>
                </div>
                <div className="flex items-start gap-2 p-3 bg-muted rounded-md border border-border md:col-span-2">
                  <span className="font-mono text-xs bg-card px-2 py-0.5 rounded border">##002#</span>
                  <span className="text-muted-foreground">Alle Weiterleitungen deaktivieren</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Code eingeben und Anruftaste drücken. Eine kurze Ansage bestätigt die Schaltung.
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3">Über das Anbieter-Portal</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {providerGuides.map((provider) => (
                  <div
                    key={provider.name}
                    className="rounded-lg border border-border bg-card overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-3 p-4 border-b border-border" style={{ backgroundColor: getProviderTint(provider, resolvedTheme) }}>
                      <ProviderLogo provider={provider} size={40} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-foreground truncate">{provider.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{provider.tagline}</div>
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      <ol className="list-decimal pl-5 space-y-1 text-sm text-muted-foreground">
                        {provider.steps.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ol>
                      <a
                        href={provider.portalUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-[#1967D2] hover:underline"
                      >
                        {provider.portalLabel} <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Tipp: Die zeitbasierte Umleitung (nur außerhalb der Öffnungszeiten) lässt sich bei Sipgate, in der Fritz!Box
                und den meisten Business-Paketen direkt als Regel einstellen.
              </p>
            </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>
        )}

        {activeTab === 'nummern' && <NumberScopeGuide />}

        {activeTab === 'leistungen' && <ServiceGroupsGuide />}

        {activeTab === 'anleitungen' && (
        <Card className="shadow-sm border-border">
          <CardHeader>
            <CardTitle className="text-lg">Anleitungen</CardTitle>
            <CardDescription>
              Schritt-für-Schritt-Hilfe zu Meta Business, WhatsApp Manager und Marketing-Einsatz der QR-Codes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" className="w-full">
              <AccordionItem value="meta-account">
                <AccordionTrigger>Meta Business-Konto einrichten</AccordionTrigger>
                <AccordionContent className="space-y-3 text-muted-foreground">
                  <p>
                    Ein Meta Business-Konto ist die Grundlage für WhatsApp Business API und die Verknüpfung mit Facebook &amp; Instagram.
                  </p>
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>
                      Öffnen Sie{' '}
                      <a
                        href="https://business.facebook.com/"
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#1967D2] hover:underline inline-flex items-center gap-1"
                      >
                        business.facebook.com <ExternalLink className="h-3 w-3" />
                      </a>
                      {' '}und melden Sie sich mit Ihrem persönlichen Facebook-Konto an.
                    </li>
                    <li>Klicken Sie auf <strong>Konto erstellen</strong> und vergeben Sie einen Unternehmensnamen (empfohlen: Ihr Salonname).</li>
                    <li>Tragen Sie Ihre geschäftliche E-Mail-Adresse und die Unternehmensdaten ein.</li>
                    <li>Bestätigen Sie die E-Mail-Adresse über den Link, den Meta Ihnen sendet.</li>
                  </ol>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="fb-page">
                <AccordionTrigger>Facebook-Seite mit dem Business-Konto verbinden</AccordionTrigger>
                <AccordionContent className="space-y-3 text-muted-foreground">
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>Gehen Sie im Business Manager auf <strong>Einstellungen → Konten → Seiten</strong>.</li>
                    <li>Wählen Sie <strong>Hinzufügen → Seite hinzufügen</strong> und tragen Sie den Namen oder URL Ihrer Salon-Seite ein.</li>
                    <li>Alternativ: <strong>Neue Seite erstellen</strong> – wählen Sie „Lokales Unternehmen / Ort” und Kategorie „Friseursalon”.</li>
                    <li>Weisen Sie sich selbst als <strong>Administrator</strong> der Seite zu.</li>
                    <li>Ergänzen Sie Öffnungszeiten, Adresse, Telefonnummer und ein Profilbild.</li>
                  </ol>
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      Die Facebook-Seite muss öffentlich veröffentlicht sein, damit WhatsApp sie verknüpfen kann.
                    </AlertDescription>
                  </Alert>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="whatsapp-profile">
                <AccordionTrigger>WhatsApp-Profil im Meta-Manager einrichten</AccordionTrigger>
                <AccordionContent className="space-y-3 text-muted-foreground">
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>
                      Öffnen Sie den{' '}
                      <a
                        href="https://business.facebook.com/wa/manage/home"
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#1967D2] hover:underline inline-flex items-center gap-1"
                      >
                        WhatsApp Manager <ExternalLink className="h-3 w-3" />
                      </a>
                      .
                    </li>
                    <li>Wählen Sie Ihr WhatsApp-Geschäftskonto (WABA) aus oder erstellen Sie eins.</li>
                    <li>Unter <strong>Telefonnummern</strong> die bereits eingerichtete Nummer auswählen.</li>
                    <li>
                      Unter <strong>Profil bearbeiten</strong> füllen Sie:
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>Profilbild (quadratisch, mind. 640×640 px)</li>
                        <li>Unternehmensbeschreibung</li>
                        <li>Adresse, Branche („Beauty, Spa &amp; Salon”)</li>
                        <li>Öffnungszeiten, Website, E-Mail</li>
                      </ul>
                    </li>
                    <li>Speichern – Meta prüft die Angaben meist innerhalb weniger Stunden.</li>
                  </ol>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="display-name">
                <AccordionTrigger>Anzeigename (Display Name) beantragen</AccordionTrigger>
                <AccordionContent className="space-y-3 text-muted-foreground">
                  <p>
                    Der Anzeigename ist das, was Kunden im WhatsApp-Chat als Absender sehen. Er muss von Meta genehmigt werden.
                  </p>
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>WhatsApp Manager → <strong>Telefonnummern → Nummer auswählen → Anzeigename bearbeiten</strong>.</li>
                    <li>Wählen Sie einen Namen, der zum Unternehmen passt (z.&nbsp;B. „Salon Mustermann”).</li>
                    <li>Vermeiden Sie reine Branchenbegriffe wie „Friseur” oder generische Worte – diese werden oft abgelehnt.</li>
                    <li>Status prüfen: <strong>Ausstehend → Genehmigt</strong>. Bei Ablehnung korrigieren und neu einreichen.</li>
                  </ol>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="business-verification">
                <AccordionTrigger>Business-Verifizierung bei Meta</AccordionTrigger>
                <AccordionContent className="space-y-3 text-muted-foreground">
                  <p>
                    Für höhere Nachrichtenlimits und ein grünes Häkchen (optional) benötigen Sie eine Business-Verifizierung.
                  </p>
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>Business Manager → <strong>Einstellungen → Sicherheitscenter → Business-Verifizierung starten</strong>.</li>
                    <li>Legen Sie Unternehmensunterlagen bereit: Handelsregisterauszug, Gewerbeanmeldung oder Steuerbescheinigung.</li>
                    <li>Telefon / E-Mail muss auf die Domain des Unternehmens registriert sein (z.&nbsp;B. info@ihr-salon.de).</li>
                    <li>Meta prüft in der Regel innerhalb 1–5 Werktagen.</li>
                  </ol>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="qr-marketing">
                <AccordionTrigger>QR-Codes im Salon nutzen (Marketing-Tipps)</AccordionTrigger>
                <AccordionContent className="space-y-3 text-muted-foreground">
                  <p>Der QR-Code oben ist Ihr direkter Draht zum Kunden – hier unsere Empfehlungen für den Einsatz:</p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li><strong>Empfangstheke:</strong> Aufsteller oder Tischkarte mit „Termin per WhatsApp buchen – einfach scannen”.</li>
                    <li><strong>Spiegel-Arbeitsplatz:</strong> Kleiner Aufkleber mit Hinweis während der Behandlung – lädt zum direkten Nachbuchen ein.</li>
                    <li><strong>Rechnung / Quittung:</strong> QR-Code aufdrucken und „Nächsten Termin buchen” ergänzen.</li>
                    <li><strong>Eingangstür / Schaufenster:</strong> Nutzen Sie Öffnungs-/Schließzeiten für automatisierte Terminanfragen 24/7.</li>
                    <li><strong>Visitenkarten:</strong> vCard-Modus nutzen, damit der Kunde Sie direkt im Adressbuch speichert.</li>
                    <li><strong>Social Media:</strong> QR-Code in Instagram- / Facebook-Story einbauen für mobile Direktbuchung.</li>
                  </ul>
                  <p className="text-sm text-muted-foreground">
                    Tipp: Der „Vorausgefüllte Text” im WhatsApp-Modus eignet sich prima für Kampagnen – z.&nbsp;B. „Ich möchte die Frühlings-Aktion buchen”.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="troubleshooting">
                <AccordionTrigger>Fehlerbehebung &amp; häufige Fragen</AccordionTrigger>
                <AccordionContent className="space-y-3 text-muted-foreground">
                  <div className="space-y-2">
                    <p className="font-medium">QR-Code öffnet WhatsApp nicht?</p>
                    <p className="text-sm">
                      Prüfen Sie, ob auf dem Smartphone WhatsApp installiert ist und die Nummer im Profil mit Ländervorwahl (z.&nbsp;B. +49…) hinterlegt wurde.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="font-medium">Kontakt wird nicht gespeichert (vCard)?</p>
                    <p className="text-sm">
                      Manche Scanner-Apps zeigen nur den Text an. Empfehlen Sie Kunden die Kamera-App direkt (iOS) oder Google Lens (Android) zu verwenden.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="font-medium">Anzeigename wurde abgelehnt?</p>
                    <p className="text-sm">
                      Wählen Sie einen eindeutigen, markenbezogenen Namen. Generische Branchenbezeichnungen führen häufig zur Ablehnung.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
        )}
      </div>

      <QrTemplatePickerDialog
        open={templateDialogOpen}
        onOpenChange={setTemplateDialogOpen}
        qrCanvas={qrRef.current?.querySelector('canvas') as HTMLCanvasElement | null}
        business={{
          name: business?.business_name || '',
          whatsappNumber: business?.whatsapp_number || '',
          phoneNumber: business?.phone_number,
          email: business?.email,
        }}
        mode={mode}
      />
    </Layout>
  );
};

export default HelpPage;
